import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trophy, Upload, X, Edit, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultFormData = {
  name: '', exam: '', rank: 1, year: new Date().getFullYear(), course_enrolled: ''
};

const AdminToppers = () => {
  const { token } = useAuth();
  const [toppers, setToppers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTopper, setEditingTopper] = useState(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [hasRankYear, setHasRankYear] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { fetchToppers(); }, []);

  const fetchToppers = async () => {
    try { const res = await axios.get(`${API_URL}/api/toppers`); setToppers(res.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const resetForm = () => {
    setEditingTopper(null);
    setFormData(defaultFormData);
    setHasRankYear(false);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const openEdit = (topper) => {
    setEditingTopper(topper);
    setFormData({
      name: topper.name,
      exam: topper.exam,
      rank: topper.rank || 1,
      year: topper.year || new Date().getFullYear(),
      course_enrolled: topper.course_enrolled || '',
    });
    setHasRankYear(!!(topper.rank || topper.year));
    setPhotoFile(null);
    setPhotoPreview(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let submitData = { ...formData };

      if (!hasRankYear) {
        const { rank, year, ...rest } = submitData;
        submitData = rest;
      }

      if (photoFile) {
        const uploadForm = new FormData();
        uploadForm.append('file', photoFile);
        const uploadRes = await axios.post(
          `${API_URL}/api/uploads?file_type=general`,
          uploadForm,
          { headers }
        );
        submitData = { ...submitData, photo_url: uploadRes.data.file_url.replace(/^https?:\/\/[^/]+/, '') };
      } else if (editingTopper) {
        submitData = { ...submitData, photo_url: editingTopper.photo_url || '' };
      }

      if (editingTopper) {
        await axios.put(`${API_URL}/api/toppers/${editingTopper.id}`, submitData, { headers });
        toast.success('Topper updated!');
      } else {
        await axios.post(`${API_URL}/api/toppers`, submitData, { headers });
        toast.success('Topper added!');
      }

      setDialogOpen(false);
      resetForm();
      fetchToppers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save topper');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this topper?')) return;
    try {
      await axios.delete(`${API_URL}/api/toppers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Topper deleted!');
      fetchToppers();
    } catch (e) {
      toast.error('Failed to delete topper');
    }
  };

  const photoSrc = editingTopper?.photo_url
    ? (editingTopper.photo_url.startsWith('http') ? editingTopper.photo_url : `${API_URL}${editingTopper.photo_url}`)
    : null;

  return (
    <AdminLayout title="Toppers">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground">Showcase top rankers.</p>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-topper-btn"><Plus className="w-4 h-4 mr-2" /> Add Topper</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingTopper ? 'Edit Topper' : 'Add Topper'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Exam</Label>
                  <Input value={formData.exam} onChange={(e) => setFormData({...formData, exam: e.target.value})} placeholder="JEE Advanced" required />
                </div>
              </div>

              {/* Rank & Year toggle */}
              <div className="flex items-center gap-2">
                <Switch checked={hasRankYear} onCheckedChange={setHasRankYear} />
                <Label>Has Rank & Year</Label>
              </div>
              {hasRankYear && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Rank (AIR)</Label>
                    <Input type="number" min={1} value={formData.rank} onChange={(e) => setFormData({...formData, rank: Number(e.target.value)})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Year</Label>
                    <Input type="number" value={formData.year} onChange={(e) => setFormData({...formData, year: Number(e.target.value)})} />
                  </div>
                </div>
              )}

              {/* Photo upload */}
              <div className="space-y-2">
                <Label>Photo</Label>
                <div
                  className="border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50 transition-colors overflow-hidden"
                  onClick={() => document.getElementById('topper-photo-input').click()}
                >
                  {photoPreview || (!photoFile && photoSrc) ? (
                    <div className="relative">
                      <img
                        src={photoPreview || photoSrc}
                        alt="Preview"
                        className="w-full h-40 object-cover object-top"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPhotoFile(null);
                          setPhotoPreview(null);
                          if (editingTopper) setEditingTopper({ ...editingTopper, photo_url: '' });
                        }}
                        className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      {photoPreview && (
                        <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                          {photoFile?.name}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
                      <Upload className="w-8 h-8" />
                      <span className="text-sm font-medium">Click to upload photo</span>
                      <span className="text-xs">JPG, PNG, GIF supported</span>
                    </div>
                  )}
                </div>
                <input
                  id="topper-photo-input"
                  type="file"
                  accept="image/jpeg,image/png,image/gif"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) { setPhotoFile(file); setPhotoPreview(URL.createObjectURL(file)); }
                    e.target.value = '';
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label>Course Enrolled <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input value={formData.course_enrolled} onChange={(e) => setFormData({...formData, course_enrolled: e.target.value})} placeholder="JEE Advanced Batch 2024" />
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    {photoFile ? 'Uploading...' : 'Saving...'}
                  </span>
                ) : (editingTopper ? 'Update Topper' : 'Add Topper')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Card key={i} className="animate-pulse"><CardContent className="h-32"></CardContent></Card>)}
        </div>
      ) : toppers.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {toppers.map((topper) => (
            <Card key={topper.id} className="text-center group relative" data-testid={`topper-${topper.id}`}>
              <CardContent className="pt-6 pb-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 overflow-hidden mb-2 relative">
                  {topper.photo_url
                    ? <img src={topper.photo_url.startsWith('http') ? topper.photo_url : `${API_URL}${topper.photo_url}`} alt={topper.name} className="w-full h-full object-cover" />
                    : <Trophy className="w-8 h-8 text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />}
                </div>
                {topper.rank && <Badge className="bg-secondary mb-2">AIR {topper.rank}</Badge>}
                <h4 className="font-semibold text-sm">{topper.name}</h4>
                <p className="text-xs text-muted-foreground">{topper.exam}{topper.year ? ` ${topper.year}` : ''}</p>
                {topper.course_enrolled && <p className="text-xs text-muted-foreground/70 mt-0.5 truncate">{topper.course_enrolled}</p>}

                {/* Edit / Delete — show on hover */}
                <div className="flex gap-1 justify-center mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => openEdit(topper)} data-testid={`edit-topper-${topper.id}`}>
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button variant="outline" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(topper.id)} data-testid={`delete-topper-${topper.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card><CardContent className="py-16 text-center"><Trophy className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" /><h3 className="font-semibold">No Toppers</h3></CardContent></Card>
      )}
    </AdminLayout>
  );
};

export default AdminToppers;
